import {
  PlaylistWindowManager,
  PlaylistWindowManagerState,
} from '@raydiant/playlist-window-manager';
import * as RE from '@raydiant/playlist-rule-engine';
import APIClient from '@raydiant/api-client-js';
import { useEffect, useCallback } from 'react';
import { BootStep } from '../App';

const adInterval = 1000 * 60 * 10; // 10 minutes

export default function useAdContent(
  bootStep: BootStep,
  windowManager: PlaylistWindowManager | null,
  apiClient: APIClient,
  logger: Logger,
): void {
  const interruptWithAdContent = useCallback(async () => {
    if (!windowManager) return;

    const {
      playlistId,
      playlists = {},
      presentations = {},
      applicationVersions = {},
      applications = {},
      themes = {},
    } = await apiClient.getDeviceAdContent();

    if (!playlistId) {
      throw new Error('Ad content has no playlist id');
    }

    const publishedRuleEngine = windowManager.getRuleEngine();
    if (!publishedRuleEngine) {
      throw new Error('Window manager has no published rule engine');
    }

    const publishedWindowManagerState = windowManager.getState();
    if (!publishedWindowManagerState) {
      throw new Error('Window manager has no published window manager state');
    }

    const adRuleEngine = RE.createRuleEngineFromAPI(
      playlistId,
      playlists,
      presentations,
      applicationVersions,
      applications,
    );

    const adWindowManagerState = new PlaylistWindowManagerState({
      presentations,
      applicationVersions,
      themes,
      publishedAt: publishedWindowManagerState.getPublishedAt(),
    });

    // Play ad content.
    logger.info(`Reinitializing window manager with ad content with interrupt`);

    await windowManager.reinitialize(adWindowManagerState, adRuleEngine, true);

    // Wait for the add window to start playing.
    await new Promise<void>((resolve) =>
      windowManager.once('window_play', resolve),
    );

    // Restore published content.
    logger.info(
      `Reinitializing window manager with published content without interrupt`,
    );

    await windowManager.reinitialize(
      publishedWindowManagerState,
      publishedRuleEngine,
      false,
    );
  }, [windowManager, apiClient, logger]);

  // Play ad content on interval.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (bootStep === 'play_playlist' && windowManager) {
      // Start ad interrupt interval.
      interval = setInterval(async () => {
        try {
          await interruptWithAdContent();
        } catch (err) {
          logger.error(
            `Failed to play ad content with '${(err as Error).message}'`,
          );
        }
      }, adInterval);
    }

    return () => {
      clearInterval(interval);
    };
  }, [bootStep, windowManager, interruptWithAdContent, logger]);
}
